import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation, Link, matchPath } from 'react-router-dom';
import { Navbar, Offcanvas, Nav, NavDropdown } from 'react-bootstrap';

import { ReactComponent as Logo } from 'assets/images/logo.svg';
import { Icon } from 'utils/icons';
import Avatar from 'components/Avatar';

import SearchInput from 'components/SearchInput';
import { menuItems, profileMenu } from 'utils/constants';

import './styles.scss';
import ALL_ROUTES from 'config/routes';
import DropDownMenu from 'components/DropDownMenu';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { useAppContext } from 'contexts/AppContext';
import { CircularProgress } from '@mui/material';
import { useBudgetMutation } from 'utils/api/mutations';
import { useBudgetQueries } from 'utils/api/queries';
import { buildPrivateBudgetShareUrl } from 'utils/budgetDeepLinks';

const Header = ({
  userData,
  menuActions,
  menuPermissions,
  budgetList,
  selectTab,
  handleLoader,
}) => {
  const {
    makeAciveTabMutation,
    createTabsMutation,
    refetchTabs,
    showAccountNumbers,
    showAiInsights,
    showDetailedView,
    handleCreateTabs,
  } = useHomeScreenContext();
  const { handleLogout, isDarkMode, toggleTheme, isUserLoggedIn } =
    useAppContext();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const searchBarRef = useRef();
  const notificationRef = useRef();
  const lastDirectMenuActionRef = useRef(null);
  const [filteredBudgets, setFilteredBudgets] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { data: notificationsResponse, refetch: refetchNotifications } =
    useBudgetQueries.useNotificationsQuery({
      enabled: Boolean(isUserLoggedIn),
      config: {
        refetchInterval: 2000,
        refetchIntervalInBackground: true,
      },
    });
  const markNotificationReadMutation =
    useBudgetMutation.useMarkNotificationReadMutation();
  const notifications = notificationsResponse?.data || [];
  const unreadNotifications = notifications.filter(item => !item.read_on);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target)
      ) {
        setFilteredBudgets([]);
        setHighlightedIndex(-1);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const goToProfileView = () => {
    navigate(ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.PROFILE);
  };

  const goToHomeScreen = () => {
    // navigate(ALL_ROUTES.HOME_SCREEN);
    window.location.href = `${ALL_ROUTES.HOME_SCREEN}`;
  };

  const handleNotificationClick = async notification => {
    try {
      if (!notification.read_on) {
        await markNotificationReadMutation.mutateAsync(notification.id);
        await refetchNotifications();
      }
      setShowNotifications(false);
      if (notification.view_state) {
        window.location.href = buildPrivateBudgetShareUrl({
          appBaseUrl: window.location.origin,
          budgetId: notification.budget,
          viewState: notification.view_state,
        });
        return;
      }
      if (notification.budget) {
        await handleCreateTabs(notification.budget);
        navigate(ALL_ROUTES.HOME_SCREEN);
      }
    } catch (error) {
      // The item remains unread so the collaborator can retry opening it.
    }
  };

  const notificationText = notification => {
    const actor = notification.actor_name || 'A collaborator';
    if (notification.kind === 'invitation') {
      return `${actor} invited you to ${notification.budget_name}.`;
    }
    if (notification.kind === 'comment_mention') {
      return `${actor} mentioned you in ${notification.budget_name}.`;
    }
    return `${actor} commented on ${notification.budget_name}.`;
  };

  const isPathMatched = path => {
    return matchPath({ path }, pathname);
  };

  const filterMenuItems = () => {
    return menuItems?.filter(
      item => item?.show || item?.show_for?.some(path => isPathMatched(path))
    );
  };

  const filterSubMenuItems = subMenu => {
    return subMenu?.filter(item => {
      if (!item?.show || !(menuPermissions?.[item.key] ?? true)) return false;

      // Hide User Settings option if already on account settings screen
      if (
        item.key === 'account-settings' &&
        pathname.includes(ALL_ROUTES.HOME_SCREEN_CHILDREN.ACCOUNT_SETTINGS)
      ) {
        return false;
      }

      // Hide User Fringes option if already on fringes screen with user type
      if (
        item.key === 'user-settings-submenu' &&
        pathname === ALL_ROUTES.FRINGES_DETAILS &&
        item.instanceType === 'user' &&
        search.includes('type=user')
      ) {
        return false;
      }

      return true;
    });
  };

  const runMenuAction = action => {
    if (menuActions?.[action]) {
      menuActions?.[action]();
    }
  };

  const getSubMenuLabel = subMenu => {
    if (subMenu?.key === 'toggle-account-numbers') {
      return showAccountNumbers
        ? 'Hide Account Numbers'
        : 'Show Account Numbers';
    }

    if (subMenu?.key === 'hide-show-insights') {
      if (!showDetailedView?.show) return 'Show AI Rate Card';
      return showAiInsights ? 'Hide AI Rate Card' : 'Show AI Rate Card';
    }

    return subMenu.label;
  };

  const getSubMenuIcon = subMenu => {
    if (subMenu?.key === 'toggle-account-numbers') {
      return (
        <Icon
          name={showAccountNumbers ? 'VisibilityOffIcon' : 'VisibilityIcon'}
          style={{ marginRight: '8px', width: '14px' }}
        />
      );
    }

    if (subMenu?.key === 'hide-show-insights') {
      return (
        <span className="app-header__ai-rate-card-icon" aria-hidden="true">
          ✦
        </span>
      );
    }

    return subMenu.icon;
  };

  const renderSubMenuContent = subMenu => {
    const icon = getSubMenuIcon(subMenu);

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {icon && <span style={{ marginRight: '8px' }}>{icon}</span>}
        {getSubMenuLabel(subMenu)}
      </div>
    );
  };

  const isSubMenuDisabled = subMenu => {
    if (subMenu?.key === 'hide-show-insights' && !showDetailedView?.show) {
      return true;
    }

    return false;
  };

  const handleOnSelect = action => {
    const lastDirectAction = lastDirectMenuActionRef.current;
    if (
      lastDirectAction?.action === action &&
      Date.now() - lastDirectAction.time < 100
    ) {
      return;
    }

    runMenuAction(action);
  };

  const handleMenuActionClick = action => event => {
    event.preventDefault();
    lastDirectMenuActionRef.current = {
      action,
      time: Date.now(),
    };
    runMenuAction(action);
  };

  const preserveGridFocusOnMouseDown = event => {
    event.preventDefault();
  };

  const handleProfileDropdownChange = action => {
    if (action === 'profile') {
      goToProfileView();
    } else if (action === 'logout') {
      handleLogout();
    }
  };

  const handleSearchChange = event => {
    const value = event?.target?.value?.trim();
    const filteredList =
      value?.length &&
      budgetList?.filter(budget =>
        budget.name?.toLowerCase().includes(value?.toLowerCase())
      );

    setFilteredBudgets(filteredList || []);
    setHighlightedIndex(filteredList && filteredList.length > 0 ? 0 : -1); // Update highlightedIndex
  };

  const handleKeyDown = event => {
    if (filteredBudgets.length > 0) {
      if (event.key === 'ArrowDown') {
        setHighlightedIndex(prevIndex =>
          prevIndex === filteredBudgets.length - 1 ? 0 : prevIndex + 1
        );
      } else if (event.key === 'ArrowUp') {
        setHighlightedIndex(prevIndex =>
          prevIndex === 0 ? filteredBudgets.length - 1 : prevIndex - 1
        );
      } else if (event.key === 'Enter' && highlightedIndex >= 0) {
        // setLoading(true);
        // handleLoader(true);
        setLoading(true);
        handleResultClick(filteredBudgets[highlightedIndex])();
        setFilteredBudgets([]);
        setHighlightedIndex(-1);
        searchBarRef.current.querySelector('input').value = '';
      }
    }
  };

  const handleResultClick = budget => async () => {
    setLoading(true);
    setHighlightedIndex(-1);
    setFilteredBudgets([]);
    searchBarRef.current.querySelector('input').value = '';

    const payload = [{ budget: budget?.id }];

    // Check if the user is on the home screen
    if (!matchPath(ALL_ROUTES.HOME_SCREEN, pathname)) {
      // If not on Home, navigate there first, then run mutation
      navigate(ALL_ROUTES.HOME_SCREEN);
      setTimeout(async () => {
        try {
          const response = await createTabsMutation.mutateAsync(payload);
          await makeAciveTabMutation.mutateAsync(response?.data?.[0]?.id);
          await refetchTabs();
        } catch (error) {
          console.error('Error creating budget tab after navigation:', error);
        } finally {
          setLoading(false);
        }
      }, 500); // Delay to allow navigation to complete
    } else {
      // If already on Home, run mutation immediately
      try {
        const response = await createTabsMutation.mutateAsync(payload);
        await makeAciveTabMutation.mutateAsync(response?.data?.[0]?.id);
        await refetchTabs();
      } catch (error) {
        console.error('Error creating budget tab:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <header className="app-header">
      <Navbar
        expand="md"
        className="d-flex px-5 align-items-center"
        onSelect={handleOnSelect}
      >
        <Navbar.Brand
          as="div"
          className="app-header__logo"
          onClick={goToHomeScreen}
        >
          <Logo />
        </Navbar.Brand>
        <Navbar.Offcanvas placement="end">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title className="d-flex align-items-center">
              <div className="app-header__logo" onClick={goToHomeScreen}>
                <Logo />
              </div>
              <Avatar className="app-header__profile" />
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="d-flex align-items-center">
            <Nav className="app-header__menu align-items-end flex-grow-1 pe-3">
              {filterMenuItems()?.map(item => {
                if (item?.sub_menu_items?.length) {
                  return (
                    <NavDropdown
                      key={item.key}
                      title={item?.label}
                      className="app-header__menu__item app-header__menu__dropdown"
                      onMouseDown={preserveGridFocusOnMouseDown}
                    >
                      {filterSubMenuItems(item.sub_menu_items)?.map(
                        (subMenu, index) => {
                          const isDisabled = isSubMenuDisabled(subMenu);

                          if (subMenu.type === 'divider') {
                            return (
                              <NavDropdown.Divider
                                key={`divider-${item.key}-${index}`}
                              />
                            );
                          } else if (subMenu.type === 'link') {
                            // Check if it's an external URL (starts with http, https, mailto, etc.)
                            const isExternalUrl = subMenu.path.match(
                              /^(https?:|mailto:|www\.)/i
                            );

                            if (isExternalUrl) {
                              // For external links, use a regular anchor tag instead of Link
                              return (
                                <NavDropdown.Item
                                  as="a"
                                  href={subMenu.path}
                                  target={subMenu.target || '_blank'}
                                  rel="noopener noreferrer"
                                  className="app-header__menu__item__submenu-item"
                                  key={`${item.key}-${subMenu.key}-${index}`}
                                  eventKey={subMenu?.key}
                                  active={false}
                                  onMouseDown={preserveGridFocusOnMouseDown}
                                >
                                  {renderSubMenuContent(subMenu)}
                                </NavDropdown.Item>
                              );
                            }

                            // For internal links, use React Router's Link
                            return (
                              <NavDropdown.Item
                                as={Link}
                                to={{
                                  pathname: subMenu.path,
                                  search: subMenu.instanceType
                                    ? `?type=${subMenu.instanceType}`
                                    : '',
                                }}
                                className="app-header__menu__item__submenu-item"
                                key={`${item.key}-${subMenu.key}-${index}`}
                                eventKey={subMenu?.key}
                                active={false}
                                onMouseDown={preserveGridFocusOnMouseDown}
                              >
                                {renderSubMenuContent(subMenu)}
                              </NavDropdown.Item>
                            );
                          } else {
                            return (
                              <NavDropdown.Item
                                className="app-header__menu__item__submenu-item"
                                key={`${item.key}-${subMenu.key}-${index}`}
                                eventKey={subMenu?.key}
                                active={false}
                                disabled={isDisabled}
                                aria-disabled={isDisabled || undefined}
                                onMouseDown={preserveGridFocusOnMouseDown}
                                onClick={
                                  isDisabled
                                    ? undefined
                                    : handleMenuActionClick(subMenu?.key)
                                }
                              >
                                {renderSubMenuContent(subMenu)}
                              </NavDropdown.Item>
                            );
                          }
                        }
                      )}
                    </NavDropdown>
                  );
                } else {
                  return (
                    <Nav.Link
                      key={item.key}
                      as="div"
                      onClick={() =>
                        (window.location.href = `/home/${item?.path}`)
                      }
                      className={
                        window?.location?.href?.split('/')[
                          window?.location?.href?.split('/').length - 1
                        ] !== 'budget-listing'
                          ? 'app-header__menu__item'
                          : 'app-header__menu__item active-header'
                      }
                      eventKey={item?.key}
                      active={false}
                    >
                      {item.label}
                    </Nav.Link>
                  );
                }
              })}
            </Nav>
          </Offcanvas.Body>
        </Navbar.Offcanvas>
        <div className="app-header__search" ref={searchBarRef}>
          {window?.location?.href?.split('/')[
            window?.location?.href?.split('/').length - 1
          ] !== 'budget-listing' && (
            <div className="d-flex gap-3">
              {loading && (
                <>
                  <CircularProgress size={30} thickness={2} color={'info'} />
                </>
              )}
              <SearchInput
                onChange={handleSearchChange}
                onFocus={handleSearchChange}
                onKeyDown={handleKeyDown}
              />
            </div>
          )}
          {filteredBudgets?.length > 0 && (
            <div className="app-header__search__result">
              {filteredBudgets?.map((budget, index) => (
                <div
                  key={`result-${budget.id}`}
                  className={`result-item ${
                    highlightedIndex === index ? 'highlighted' : ''
                  }`}
                  onClick={handleResultClick(budget)}
                >
                  {budget?.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="app-header__notifications" ref={notificationRef}>
          <button
            aria-expanded={showNotifications}
            aria-label={`Notifications${
              unreadNotifications.length
                ? ` (${unreadNotifications.length} unread)`
                : ''
            }`}
            className={`app-header__notification-button ${
              unreadNotifications.length ? 'has-unread' : ''
            }`}
            onClick={() => setShowNotifications(current => !current)}
            type="button"
          >
            <Icon name="NotificationIcon" />
            {unreadNotifications.length ? (
              <span className="app-header__notification-count">
                {unreadNotifications.length > 9
                  ? '9+'
                  : unreadNotifications.length}
              </span>
            ) : null}
          </button>
          {showNotifications ? (
            <div className="app-header__notification-menu" role="menu">
              <div className="app-header__notification-menu-title">
                Notifications
              </div>
              {notifications.length ? (
                notifications.slice(0, 20).map(notification => (
                  <button
                    className={`app-header__notification-item ${
                      notification.read_on ? '' : 'is-unread'
                    }`}
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    role="menuitem"
                    type="button"
                  >
                    <strong>{notificationText(notification)}</strong>
                    {(notification.kind === 'cell_comment' ||
                      notification.kind === 'comment_mention') &&
                    notification.body ? (
                      <span>{notification.body}</span>
                    ) : null}
                  </button>
                ))
              ) : (
                <div className="app-header__notification-empty">
                  You’re all caught up.
                </div>
              )}
            </div>
          ) : null}
        </div>
        {/* {loading && (
          <>
            <div className="loader-backdrop"></div>
            <div className="loader-wrapper">
              <div className="loader-square">
                <CornerSpinner />
              </div>
            </div>
          </>
        )} */}
        <button
          type="button"
          className="app-header__theme-toggle"
          onClick={toggleTheme}
          aria-label={
            isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
          }
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <span
            className={`app-header__theme-toggle-option ${
              !isDarkMode ? 'is-active' : ''
            }`}
          >
            <Icon name="SunIcon" className="app-header__theme-toggle-icon" />
          </span>
          <span
            className={`app-header__theme-toggle-option ${
              isDarkMode ? 'is-active' : ''
            }`}
          >
            <Icon name="MoonIcon" className="app-header__theme-toggle-icon" />
          </span>
        </button>
        <DropDownMenu
          className="app-header__profile-dropdown"
          menuOptions={profileMenu}
          onChange={handleProfileDropdownChange}
          align={{ sm: 'end' }}
          toggleIcon={<></>}
          toggleLabel={
            <Avatar
              className="app-header__profile"
              name={userData?.name || userData?.email}
              image={
                userData?.profile_image ? (
                  <img src={userData?.profile_image} alt="profile-pic" />
                ) : null
              }
            />
          }
        />
        <Navbar.Toggle aria-controls="Navbar-expand" />
      </Navbar>
    </header>
  );
};

Header.propTypes = {
  userData: PropTypes.object,
  menuActions: PropTypes.object.isRequired,
  menuPermissions: PropTypes.object.isRequired,
  budgetList: PropTypes.array,
  selectTab: PropTypes.func.isRequired,
  handleLoader: PropTypes.func.isRequired,
};

Header.defaultProps = {
  userData: {},
  budgetList: [],
};

export default Header;
